import { defaultDoublePitch, defaultSinglePitch } from './pitch'
import { americanFootballEventTypes } from './events'
import { outcomeTypes } from '../../outcomes/data_types'
import { pitchSetupTypes } from '../../pitches/data_types'
import { FormattedEventData } from '../../events/types'
import { FlightEventTypeGroup } from '../../events/flight/data_types'
import dataTypes from './data_types_config.json'
import {
  getMetricTypeGroup,
  getMetricTypesConfigFromJson,
  metricTypesConfig
} from '../../metrics/data_types'
import { sportableColors } from '../../../constants/sportableColors'
import { gameEventTypes } from '../../events/game/data_types'
import { gameEventSubTypes } from '../../events/game/subType/data_types'

export const americanFootballProps = {
  eventTypes: americanFootballEventTypes,
  playerSummaryMetricTypes: getMetricTypeGroup(
    getMetricTypesConfigFromJson(
      dataTypes.playerSummary.metrics,
      metricTypesConfig
    )
  ),
  pitch: {
    default: defaultSinglePitch,
    defaultDoublePitch: defaultDoublePitch,
    pitchPointCount: 30,
    setupTypes: pitchSetupTypes,
    minPadding2D: 70,
    minPadding3D: 50,
    cameraYScale: 0.5,
    textureCanvasScale: 11,
    tenYardMarkings: 10,
    enableBench: true,
    enableRotate: true,
    defaultDrillRegion: {
      P1: 'P26',
      P2: 'P14',
      P3: 'P13',
      P4: 'P1',
      P5: null,
      P6: null,
      P7: null,
      P8: null
    } as const,
    drillsGoalsOrPosts: {
      enabled: false,
      dimensions: {
        large: {
          width: 7.32,
          height: 2.44,
          depth: 2.44
        },
        small: {
          width: 1.914,
          height: 1.914,
          depth: 1.61
        }
      }
    },
    drillSnapLines: [
      'P1',
      'P2',
      'P3',
      'P4',
      'P5',
      'P6',
      'P7',
      'P8',
      'P9',
      'P10',
      'P11',
      'P12',
      'P13'
    ],
    autosetupTagPositions: ['P7', 'P13', 'P15'],
    pitchCornerKeys: {
      topLeft: 'P26',
      topRight: 'P14',
      bottomRight: 'P13',
      bottomLeft: 'P1'
    },
    leftAutosetupRegion: {
      topLeft: 'P26',
      topRight: 'P20',
      bottomRight: 'P7',
      bottomLeft: 'P1'
    },
    rightAutosetupRegion: {
      topLeft: 'P20',
      topRight: 'P14',
      bottomRight: 'P13',
      bottomLeft: 'P7'
    },
    leftTeamRegion: {
      topLeft: 'P26',
      topRight: 'P25',
      bottomRight: 'P2',
      bottomLeft: 'P1'
    },
    rightTeamRegion: {
      topLeft: 'P15',
      topRight: 'P14',
      bottomRight: 'P13',
      bottomLeft: 'P12'
    }
  },
  features: {
    drillsEnabled: true,
    targetEvents: ['pass'],
    positionNumbers: false,
    maxPlayers: 46,
    defaultUnitSystem: 'American',
    keyboardShortcuts: {
      screenName: 'Validation - Kick Detail Card',
      shortcuts: {
        c: {
          condition: (
            formattedEvent: FormattedEventData,
            flightTypes: FlightEventTypeGroup
          ) => {
            return (
              formattedEvent &&
              (formattedEvent.type.selected?.value ===
                flightTypes.items.pass?.value ||
                formattedEvent.type.selected?.value ===
                  gameEventTypes.items.manualPass?.value ||
                formattedEvent.type.selected?.value ===
                  gameEventTypes.items.touchDown?.value)
            )
          },
          outcome: outcomeTypes.items.complete?.value,
          subType: gameEventSubTypes.items.manualPassComplete?.value
        },
        x: {
          condition: (
            formattedEvent: FormattedEventData,
            flightTypes: FlightEventTypeGroup
          ) => {
            return (
              formattedEvent &&
              formattedEvent.type.selected?.value ===
                flightTypes.items.pass?.value
            )
          },
          outcome: outcomeTypes.items.incomplete?.value
        },
        // t: {
        //   condition: (
        //     formattedEvent: FormattedEventData,
        //     flightTypes: FlightEventTypeGroup
        //   ) => {
        //     return (
        //       formattedEvent &&
        //       formattedEvent.type.selected?.value ===
        //         flightTypes.items.pass?.value
        //     )
        //   },
        //   outcome: outcomeTypes.items.touchdown?.value
        // },
        i: {
          condition: (
            formattedEvent: FormattedEventData,
            flightTypes: FlightEventTypeGroup
          ) => {
            return (
              formattedEvent &&
              (formattedEvent.type.selected?.value ===
                flightTypes.items.pass?.value ||
                formattedEvent.type.selected?.value ===
                  gameEventTypes.items.manualPass?.value)
            )
          },
          outcome: outcomeTypes.items.interception?.value,
          subType: gameEventSubTypes.items.manualPassIntercepted?.value
        }
      },
      codingShortcuts: {
        p: {
          type: 'createGameEvent',
          payload:
            americanFootballEventTypes.items.game.props.types.items.manualPass
        },
        s: {
          type: 'createGameEvent',
          payload:
            americanFootballEventTypes.items.game.props.types.items.handOff
        },
        f: {
          type: 'createGameEvent',
          payload:
            americanFootballEventTypes.items.game.props.types.items.tackle
        },
        n: {
          type: 'createGameEvent',
          payload:
            americanFootballEventTypes.items.game.props.types.items.penalty
        },
        w: {
          type: 'createGameEvent',
          payload:
            americanFootballEventTypes.items.game.props.types.items.huddleBreak
        },
        e: {
          type: 'createGameEvent',
          payload:
            americanFootballEventTypes.items.game.props.types.items.lineSet
        },
        d: {
          type: 'createGameEvent',
          payload:
            americanFootballEventTypes.items.game.props.types.items.qbRush
        },
        g: {
          type: 'createGameEvent',
          payload:
            americanFootballEventTypes.items.game.props.types.items.qbSack
        }
      }
    },
    recentEventTableMetrics: [
      'inPitchDistance',
      'inPitchHangTime',
      'speed',
      'territorialGain'
    ],
    broadcastIntegrationAvailable: false,
    noOfPlayersInStartingLineUp: null,
    scoreboard: false,
    homeSideCheck: false,
    bibs: {
      '1': {
        name: 'No Bib',
        colour: null
      },
      '2': {
        name: 'Offense',
        colour: sportableColors.colors.sportableRed
      },
      '3': {
        name: 'Defense',
        colour: sportableColors.colors.sportableBlue
      }
    }
  } as const
}
